import React, { useEffect, useRef, useMemo } from "react";
import useFormItemStatus from "antd/es/form/hooks/useFormItemStatus";
import "./InputTelefoneInternacional.css";

export default function InputTelefoneInternacional(props) {
	const {
		value,
		onChange,
		placeholder,
		defaultCountry = "br",
		...restProps
	} = props;

	const inputRef = useRef(null);
	const itiRef = useRef(null);
	const validationStateRef = useRef({ isValid: false, phoneNumber: "" });
	const lastAppliedValueRef = useRef(null);
	const valueRef = useRef(value);

	// Usar ref para onChange para evitar re-renders
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const formStatus = useFormItemStatus();

	const classes = useMemo(() => {
		const classes = ["ant-input"];
		if (formStatus.status === "error") {
			classes.push("ant-input-status-error");
		}
		return classes.join(" ");
	}, [formStatus]);

	// Carregar scripts necessários
	useEffect(() => {
		// Carregar CSS apenas uma vez
		if (!document.querySelector('link[href="/intlTelInput.css"]')) {
			const cssLink = document.createElement("link");
			cssLink.rel = "stylesheet";
			cssLink.href = "/intlTelInput.css";
			document.head.appendChild(cssLink);
		}

		// Carregar JS se não estiver carregado
		if (
			!window.intlTelInput &&
			!document.querySelector('script[src="/intlTelInput.min.js"]')
		) {
			// Primeiro carregar utils.js
			if (!document.querySelector('script[src="/utils.js"]')) {
				const utilsScript = document.createElement("script");
				utilsScript.src = "/utils.js";
				utilsScript.onload = () => {
					// Depois carregar intlTelInput.min.js
					const script = document.createElement("script");
					script.src = "/intlTelInput.min.js";
					script.onload = () => {
						window.dispatchEvent(new Event("intlTelInputLoaded"));
					};
					document.head.appendChild(script);
				};
				document.head.appendChild(utilsScript);
			} else {
				// Se utils já existe, carregar intlTelInput
				const script = document.createElement("script");
				script.src = "/intlTelInput.min.js";
				script.onload = () => {
					window.dispatchEvent(new Event("intlTelInputLoaded"));
				};
				document.head.appendChild(script);
			}
		}

		return () => {
			// Não remover CSS/JS globalmente, apenas limpar listeners
		};
	}, []);

	// Observer removido para simplificar
	const initialValueSetRef = useRef(false);

	useEffect(() => {
		// Função para inicializar o intl-tel-input
		const initializeIntlTelInput = () => {
			if (inputRef.current && !itiRef.current && window.intlTelInput) {
				// Inicializar intl-tel-input seguindo a documentação oficial
				itiRef.current = window.intlTelInput(inputRef.current, {
					initialCountry: defaultCountry,
					preferredCountries: [
						"br",
						"us",
						"ar",
						"cl",
						"co",
						"mx",
						"pe",
					],
					utilsScript: "/utils.js", // Necessário para formatação automática
					separateDialCode: true,
					nationalMode: true, // Formatação nacional
					autoPlaceholder: "aggressive", // Formatação mais agressiva
					placeholderNumberType: "MOBILE",
					// Anexa o dropdown de países ao <body> (em vez de inline,
					// no container do input) pra ele NÃO ser cortado pelo
					// overflow:auto/maxHeight do antd Modal. A lib posiciona o
					// dropdown de forma absoluta e o reposiciona no scroll
					// (add/removeEventListener "scroll"). Seguro também na tela
					// de registro (página cheia) — lá o body é o contexto natural.
					dropdownContainer: document.body,
				});

				// Configurar valor inicial apenas uma vez (quando o componente é montado)
				// Isso evita reconfigurações desnecessárias durante a digitação
				const initialValue = valueRef.current;
				if (
					!initialValueSetRef.current &&
					initialValue &&
					typeof initialValue === "string" &&
					initialValue.startsWith("+")
				) {
					initialValueSetRef.current = true;
					lastAppliedValueRef.current = initialValue;
					// Aguardar um pouco para garantir que a biblioteca está pronta
					setTimeout(() => {
						if (itiRef.current) {
							itiRef.current.setNumber(initialValue);
						}
					}, 100);
				}

				// Event listener para mudanças - usando API oficial da biblioteca
				const handleInputChange = () => {
					if (itiRef.current && onChangeRef.current) {
						// Usar métodos oficiais da biblioteca
						const phoneNumber = itiRef.current.getNumber();
						const isValid = itiRef.current.isValidNumber();
						const countryData =
							itiRef.current.getSelectedCountryData();

						// Usar validação nativa da biblioteca para todos os países
						let finalIsValid = isValid;

						// Validação adicional usando utils se disponível
						if (phoneNumber && window.intlTelInputUtils) {
							try {
								const validationResult =
									window.intlTelInputUtils.isValidNumber(
										phoneNumber,
										countryData.iso2,
									);
								finalIsValid = validationResult;
							} catch (e) {
								// Se houver erro na validação utils, usar resultado da biblioteca
								finalIsValid = isValid;
							}
						}

						// Tentar obter número formatado nacionalmente para exibição
						let nationalNumber = phoneNumber;
						if (
							window.intlTelInputUtils &&
							phoneNumber &&
							finalIsValid
						) {
							try {
								nationalNumber =
									window.intlTelInputUtils.formatNumber(
										phoneNumber,
										countryData.iso2.toUpperCase(),
										window.intlTelInputUtils.numberFormat
											.NATIONAL,
									);
							} catch (e) {
								// Erro na formatação, usar valor original
							}
						}

						// Aplicar o valor formatado nacionalmente no input para exibição
						if (
							nationalNumber &&
							nationalNumber !== phoneNumber &&
							finalIsValid
						) {
							inputRef.current.value = nationalNumber;
						}

						// Armazenar estado de validação para uso no validador
						// phoneNumber já está no formato E.164 (+código+número)
						validationStateRef.current = {
							isValid: finalIsValid,
							phoneNumber: phoneNumber, // Número internacional no formato E.164
							nationalNumber: nationalNumber, // Número formatado nacionalmente para exibição
							countryCode: countryData.iso2,
							countryName: countryData.name,
						};

						// Armazenar estado de validação globalmente para uso no validador
						window.phoneValidationState =
							validationStateRef.current;

						// Salvar o número no formato internacional (+código+número)
						// phoneNumber já está no formato E.164 (ex: +5511999999999)
						onChangeRef.current({
							target: {
								value: phoneNumber, // Salvar no formato internacional
								isValid: finalIsValid,
								countryCode: countryData.iso2,
								countryName: countryData.name,
							},
						});
					}
				};

				// Aplicar bandeiras de forma robusta
				const applyFlags = () => {
					const flagElements =
						inputRef.current.parentElement.querySelectorAll(
							".iti__flag",
						);
					flagElements.forEach((flag) => {
						flag.style.backgroundImage = 'url("/img/flags.png")';
						flag.style.backgroundSize = "5652px 15px";
						flag.style.width = "24px";
						flag.style.height = "18px";
						flag.style.marginRight = "8px";
						flag.style.display = "inline-block";
					});
				};

				// Event listeners usando API oficial
				inputRef.current.addEventListener("input", handleInputChange);

				// Listener para quando utils carregar
				inputRef.current.addEventListener("utilsLoaded", () => {
					// Utils carregado, formatação ativada
				});

				// Listener específico para mudança de país
				const handleCountryChange = (e) => {
					// Aplicar bandeiras após mudança de país
					setTimeout(applyFlags, 100);
					// Disparar handleInputChange também
					handleInputChange();
				};

				// Adicionar listener para countrychange no elemento pai
				inputRef.current.parentElement.addEventListener(
					"countrychange",
					handleCountryChange,
				);

				// Também adicionar no input para garantir
				inputRef.current.addEventListener(
					"countrychange",
					handleCountryChange,
				);

				// Listener para cliques no dropdown de países
				const handleDropdownClick = (e) => {
					// Se clicou em um país, aguardar um pouco e verificar mudança
					if (e.target.classList.contains("iti__country")) {
						setTimeout(() => {
							handleCountryChange(e);
						}, 50);
					}
				};

				// Adicionar listener para cliques no dropdown
				inputRef.current.parentElement.addEventListener(
					"click",
					handleDropdownClick,
				);

				// Aplicar bandeiras apenas uma vez após inicialização
				setTimeout(applyFlags, 200);
			}
		};

		// Listener para quando o script carregar
		const handleScriptLoad = () => {
			initializeIntlTelInput();
		};

		// Aguardar utils carregar antes de inicializar
		const waitForUtils = () => {
			if (window.intlTelInput && window.intlTelInputUtils) {
				// Aguardar um pouco mais para garantir que utils está totalmente carregado
				setTimeout(() => {
					initializeIntlTelInput();
				}, 200);
			} else {
				setTimeout(waitForUtils, 100);
			}
		};

		// Tentar inicializar imediatamente se já estiver disponível
		if (window.intlTelInput && window.intlTelInputUtils) {
			setTimeout(() => {
				initializeIntlTelInput();
			}, 200);
		} else {
			// Se não estiver disponível, aguardar o evento
			window.addEventListener("intlTelInputLoaded", waitForUtils);
		}

		// Cleanup
		return () => {
			window.removeEventListener("intlTelInputLoaded", handleScriptLoad);
			if (itiRef.current) {
				itiRef.current.destroy();
				itiRef.current = null;
			}
			initialValueSetRef.current = false;
		};
	}, [defaultCountry]); // Removido value das dependências para evitar reinicializações

	useEffect(() => {
		valueRef.current = value;
	}, [value]);

	useEffect(() => {
		if (
			!value ||
			typeof value !== "string" ||
			!value.startsWith("+") ||
			!itiRef.current
		) {
			return;
		}
		if (value === lastAppliedValueRef.current) return;
		lastAppliedValueRef.current = value;
		itiRef.current.setNumber(value);
	}, [value]);

	// Removido useEffect que estava causando conflito com mudança de país
	// O intl-tel-input deve gerenciar seu próprio estado

	return (
		<div className="intl-tel-input-wrapper" style={{ width: "100%" }}>
			<input
				ref={inputRef}
				type="tel"
				className={classes}
				placeholder={placeholder || "Digite seu telefone"}
				style={{ width: "100%", boxSizing: "border-box" }}
				{...restProps}
			/>
		</div>
	);
}
